import {
  ServiceCharge,
  ServiceChargePut,
  ServiceChargeFilter,
} from "callable-data-objects";
import Page from "callable-data-objects/dist/services/Page";
import { useOrganisation } from "Hooks/Endpoints";
import { APIOrganisationChargeContext } from "Providers/APIContexts";
import React, { useState } from "react";
import { APIOrganisationProps, DefaultProviderProps } from "./types";
import moment from "moment";
import { useAPI } from "./APIProvider";

const APIOrgChargeProvider = ({ children }: DefaultProviderProps) => {
  const api = useAPI();
  const { organisation, select: selectOrganisation }: APIOrganisationProps =
    useOrganisation();
  const [charges, setCharges] = useState<Page<ServiceCharge> | undefined>();
  const [charge, setCharge] = useState<ServiceCharge | undefined>();
  const [filter, setFilter] = useState<ServiceChargeFilter>({
    date: moment().format("YYYY-MM-DD"),
    page: 0,
    max: 25,
  });

  React.useEffect(() => {
    if (organisation && filter) {
      doFilter(organisation.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation, filter]);

  function doFilter(id: string) {
    api.organisationService
      .charge(id)
      .filter(filter)
      .then((resp: Page<ServiceCharge>) => {
        setCharges(resp);
      });
  }
  async function select(id: string) {
    if (organisation) {
      const c = await api.organisationService.charge(organisation.id).get(id);
      setCharge(c);
    }
  }

  async function remove(id: string) {
    if (organisation) {
      await api.organisationService.charge(organisation.id).delete(id);
      doFilter(organisation.id);
    }
  }

  function unselect() {
    setCharge(undefined);
  }

  async function update(payload: ServiceChargePut) {
    if (organisation && charge) {
      await api.organisationService
        .charge(organisation.id)
        .put(charge.id, payload);
      select(charge.id);
    }
  }

  async function search(payload: ServiceChargeFilter) {
    const f = {
      ...filter,
      ...payload,
    };
    console.log(f);
    setFilter(f);
  }

  async function downloadServices() {
    if (organisation) {
      const csv: string = await api.organisationService
        .charge(organisation.id)
        .downloadServiceCharges(filter);

      const url = window.URL.createObjectURL(new Blob([csv]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Services.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    }
  }

  async function downloadProducts() {
    if (organisation) {
      const csv: string = await api.organisationService
        .charge(organisation.id)
        .downloadProductTable();

      const url = window.URL.createObjectURL(new Blob([csv]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Products.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    }
  }

  async function downloadRates() {
    if (organisation) {
      const csv: string = await api.organisationService
        .charge(organisation.id)
        .downloadRatingTable();

      const url = window.URL.createObjectURL(new Blob([csv]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Rates.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    }
  }

  async function downloadCDRs() {
    if (organisation) {
      const date = moment(filter.date, "yyyy-MM-dd");
      const year = date.format("yyyy");
      const month = date.format("MM");

      const csv: string = await api.organisationService
        .charge(organisation.id)
        .downloadCDRs(year, month);

      const url = window.URL.createObjectURL(new Blob([csv]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `CDR-${year}${month}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    }
  }

  return (
    <APIOrganisationChargeContext.Provider
      value={{
        organisation,
        selectOrganisation,
        charges,
        charge,
        select,
        remove,
        unselect,
        update,
        search,
        downloadServices,
        downloadProducts,
        downloadRates,
        downloadCDRs,
        filter,
      }}
    >
      {children}
    </APIOrganisationChargeContext.Provider>
  );
};

export default APIOrgChargeProvider;
